import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Spin, Tag, Modal, message } from 'antd';
import { FullscreenExitOutlined, ReloadOutlined, AlertOutlined, GlobalOutlined } from '@ant-design/icons';
import { io } from 'socket.io-client';
import ReactEChartsCore from 'echarts-for-react';
import * as echarts from 'echarts';
import { dashboardApi, charging } from '../../services/api';
import chinaJson from '../../assets/china.json';
import guizhouJson from '../../assets/guizhou.json';
import './BigScreen.css';
import StationVideoGrid from './StationVideoGrid';
import StationVideoFusion from './StationVideoFusion';
import EzvizPlayer from '../../components/EzvizPlayer';

// ECharts 5 不内置地图：geojson 本地打包注册（不走 CDN），全国 + 贵州下钻两级
echarts.registerMap('china', chinaJson);
echarts.registerMap('guizhou', guizhouJson);

const DESIGN_W = 1920;
const DESIGN_H = 1080;
const TWIN_VIEWERS = {
  25: '/twin/yushe/cesium.html',
};

const LEVEL_STYLE = {
  '紧急': 'emergency', emergency: 'emergency',
  '严重': 'critical', critical: 'critical',
  '警告': 'warning', warning: 'warning',
  '提示': 'info', info: 'info',
};

const STATION_COLORS = { park: '#40a9ff', pv: '#ffd666', storage: '#b37feb', charging: '#31e8a3' };
const STATION_NAMES = { park: '园区', pv: '光伏站', storage: '储能站', charging: '充电站' };

// 调度事件看板：终态状态集合与 30 天展示窗口（已结束且超窗的历史事件不上屏）
const DR_FINISHED = ['assessed', 'settled', 'cancelled'];
const DP_FINISHED = ['已完成', '执行成功', '执行失败', '已取消'];
const EVENT_WINDOW_MS = 30 * 86400000;

function formatClock() {
  return new Date().toLocaleString('zh-CN', { hour12: false });
}

function formatAlarmTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('zh-CN', { hour12: false, month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/**
 * 站名差异化显示：充电场站档案名统一为「好运好电重卡充电站（XX站）」或
 * 「好运好电重卡充电服务站（XX站）」，提取括号内的区分段（如"水城经开区站"）；
 * 无括号时剥公共前缀兜底；全名仍通过 title 悬浮展示
 */
function shortStationName(name) {
  if (!name) return '-';
  const text = String(name);
  const m = text.match(/[（(]([^（）()]+)[）)]/);
  if (m && m[1]) return m[1].trim();
  const stripped = text.replace(/^好运好电重卡充电(服务)?站/, '').trim();
  return stripped || text;
}

/** 数字滚动（翻牌）组件：纯 rAF 缓动补间，不引第三方库；非数值显示 "-" */
function RollNumber({ value, decimals = 0, duration = 900 }) {
  const [display, setDisplay] = useState(null);
  const prevRef = useRef(0);
  useEffect(() => {
    const to = Number(value);
    if (!Number.isFinite(to)) { setDisplay(null); return undefined; }
    const from = Number(prevRef.current) || 0;
    prevRef.current = to;
    let raf;
    const start = performance.now();
    const tick = (t) => {
      const k = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - k, 3);
      setDisplay(from + (to - from) * eased);
      if (k < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);

  if (value === null || value === undefined || !Number.isFinite(Number(value))) {
    return <span className="bs-roll">-</span>;
  }
  return (
    <span className="bs-roll">
      {Number(display ?? 0).toLocaleString('zh-CN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}
    </span>
  );
}

function ChargingCarbonValue({ value, decimals = 2 }) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return <b>暂无数据</b>;
  return <b><RollNumber value={value} decimals={decimals} /> tCO₂e</b>;
}

/** 科技切角面板：四角霓虹描边 + 标题装饰条 */
function Panel({ title, extra, children, className = '' }) {
  return (
    <div className={`bs-panel ${className}`}>
      <div className="bs-panel-head">
        <span className="bs-panel-title">{title}</span>
        {extra && <span className="bs-panel-extra">{extra}</span>}
      </div>
      <div className="bs-panel-body">{children}</div>
    </div>
  );
}

function EmptyBox({ text = '暂无数据' }) {
  return <div className="bs-empty">{text}</div>;
}

/**
 * 图表包装：挂载后强制 resize（rAF + 两次延迟）。
 * 大屏面板为 flex 弹性尺寸，ECharts 在容器尺寸未收敛时初始化会把
 * graph/曲线按 0 尺寸布局且不会自动恢复，导致能流图/曲线首屏空白。
 */
function BSChart({ option, onEvents, style }) {
  const ref = useRef(null);
  useEffect(() => {
    const resize = () => ref.current?.getEchartsInstance()?.resize();
    const raf = requestAnimationFrame(resize);
    const t1 = setTimeout(resize, 300);
    const t2 = setTimeout(resize, 1200);
    return () => { cancelAnimationFrame(raf); clearTimeout(t1); clearTimeout(t2); };
  }, []);
  return <ReactEChartsCore ref={ref} option={option} style={style || { height: '100%' }} notMerge onEvents={onEvents} />;
}

export default function BigScreen() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clock, setClock] = useState(formatClock());
  const [stationOptions, setStationOptions] = useState([]);
  const [viewMode, setViewMode] = useState('network');
  const [stationPanel, setStationPanel] = useState('twin');
  const [activeCamera, setActiveCamera] = useState(null);
  const [chargingStatus, setChargingStatus] = useState([]);
  const [parkId, setParkId] = useState(null);
  const [overview, setOverview] = useState(null);
  const [flow, setFlow] = useState(null);
  const [liveAlarms, setLiveAlarms] = useState([]);
  const [trendSeries, setTrendSeries] = useState({ labels: [], load: [], pv: [], charging: [] });
  const [mapStations, setMapStations] = useState([]);
  const [topStations, setTopStations] = useState({ list: [], unit: 'kWh' });
  const [trend30, setTrend30] = useState({ labels: [], load: [], pv: [], charging: [] });
  // china | guizhou（贵州下钻）；持久化到 localStorage，刷新/分辨率切换后保持视图
  const [mapLevel, setMapLevel] = useState(() => (
    localStorage.getItem('bs_map_level') === 'guizhou' ? 'guizhou' : 'china'
  ));
  const [scale, setScale] = useState(1);
  const flashRef = useRef(null);
  const requestSeqRef = useRef(0);
  const slowSeqRef = useRef(0);
  const wsErrorNotifiedRef = useRef(false);

  const parkIdRef = useRef(null);

  const loadData = useCallback(async (sid) => {
    const seq = ++requestSeqRef.current;
    const id = sid ?? parkIdRef.current;
    try {
      const params = id ? { space_id: id } : {};
      const [ov, fl, al] = await Promise.all([
        dashboardApi.overview(params),
        dashboardApi.energyFlow(params),
        dashboardApi.liveAlarms({ ...params, limit: 20 }),
      ]);
      if (seq !== requestSeqRef.current) return;
      setOverview(ov.data);
      setFlow(fl.data);
      setLiveAlarms(al.data || []);
      setTrendSeries({
        labels: ov.data?.trend?.labels || [],
        load: ov.data?.trend?.series?.load || [],
        pv: ov.data?.trend?.series?.pv || [],
        charging: ov.data?.trend?.series?.charging || [],
      });
      // 后端在全网聚合时返回 park.id=0（虚拟园区），不能用真值判断，否则页签高亮丢失
      if (ov.data?.park && ov.data.park.id !== null && ov.data.park.id !== undefined) {
        parkIdRef.current = ov.data.park.id;
        setParkId(ov.data.park.id);
      }
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }, []);

  // 低频面板（地图站点/TOP榜/30日趋势）：60 秒一轮，避免每次 10s 轮询都打重聚合
  const loadSlow = useCallback(async () => {
    const seq = ++slowSeqRef.current;
    try {
      const stationId = parkIdRef.current;
      const [ms, ts, t30] = await Promise.all([
        dashboardApi.mapStations(),
        dashboardApi.topStations({ metric: 'charging_energy', limit: 10 }),
        dashboardApi.trend30d({ days: 30, ...(stationId ? { space_id: stationId } : {}) }),
      ]);
      if (seq !== slowSeqRef.current) return;
      setMapStations(ms.data || []);
      setTopStations({ list: ts.data?.list || [], unit: ts.data?.unit || 'kWh' });
      setTrend30({
        labels: t30.data?.labels || [],
        load: t30.data?.series?.load || [],
        pv: t30.data?.series?.pv || [],
        charging: t30.data?.series?.charging || [],
      });
    } catch { /* 低频面板失败不打断主轮询，下轮自愈 */ }
  }, []);

  const loadChargingStatus = useCallback(async () => {
    const res = await charging.status();
    setChargingStatus(res.data || []);
  }, []);

  const loadStations = useCallback(async () => {
    const [pileRes, statusRes] = await Promise.all([charging.list(), charging.status()]);
    const statusList = statusRes.data || [];
    setChargingStatus(statusList);
    const spaceByCode = new Map((pileRes.data || []).map((p) => [p.code, p.space_id]));
    const grouped = new Map();
    for (const pile of statusList) {
      const id = spaceByCode.get(pile.code);
      if (!id || !pile.station_name) continue;
      const current = grouped.get(id) || { id, name: pile.station_name, pileCount: 0 };
      current.pileCount += 1;
      grouped.set(id, current);
    }
    setStationOptions([...grouped.values()].sort((a, b) => a.name.localeCompare(b.name, 'zh-CN')));
  }, []);

  useEffect(() => {
    (async () => {
      await loadStations();
      await loadData();
      await loadSlow();
    })();
    const t1 = setInterval(() => { loadData(parkIdRef.current); loadChargingStatus(); }, 10000);
    const t2 = setInterval(() => setClock(formatClock()), 1000);
    const t3 = setInterval(loadSlow, 60000);
    return () => { clearInterval(t1); clearInterval(t2); clearInterval(t3); };
  }, [loadData, loadStations, loadSlow, loadChargingStatus]);

  useEffect(() => {
    setActiveCamera(null);
    // parkId=0 是"全网总览"虚拟页签，属于有效选择，不能用真值判断拦截
    if (parkId === null || parkId === undefined) return;
    parkIdRef.current = parkId;
    setLoading(true);
    loadData(parkId);
    loadSlow();
  }, [parkId, loadData, loadSlow]);

  // 1920×1080 设计基准，transform scale 等比适配任意分辨率
  useEffect(() => {
    const onResize = () => setScale(Math.min(window.innerWidth / DESIGN_W, window.innerHeight / DESIGN_H));
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 大屏模式：去除浏览器默认边距、隐藏滚动条，离开时还原
  useEffect(() => {
    document.body.classList.add('bigscreen-mode');
    return () => document.body.classList.remove('bigscreen-mode');
  }, []);

  useEffect(() => {
    const onCameraMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'yushe-camera' && event.data.camera?.id) {
        setActiveCamera(event.data.camera);
      }
    };
    window.addEventListener('message', onCameraMessage);
    return () => window.removeEventListener('message', onCameraMessage);
  }, []);

  useEffect(() => {
    const base = process.env.REACT_APP_API_BASE || window.location.origin;
    const socket = io(base.replace(/\/$/, ''), {
      transports: ['websocket', 'polling'],
      path: '/socket.io/',
      auth: { token: localStorage.getItem('qhy_token') || '' },
    });
    socket.on('connect_error', (err) => {
      // 鉴权失败（token 过期）：清理登录态并跳回登录页，终止重连风暴
      if (String(err?.message || '').toLowerCase().includes('unauthorized')) {
        localStorage.removeItem('qhy_token');
        localStorage.removeItem('qhy_user');
        socket.disconnect();
        window.location.hash = '#/login';
        return;
      }
      // 其他连接异常只提示一次，避免无限重连反复弹窗
      if (!wsErrorNotifiedRef.current) {
        wsErrorNotifiedRef.current = true;
        message.error('实时连接未授权或已断开，大屏实时推送不可用');
      }
    });
    socket.emit('subscribe', 'dashboard');
    socket.emit('subscribe', 'alarms');
    socket.on('alarm_update', (payload) => {
      setLiveAlarms((prev) => {
        const next = [{ ...payload, priority: 3, occurred_at: payload.occurred_at || new Date().toISOString() }, ...prev.filter((a) => a.id !== payload.id)];
        return next.slice(0, 30);
      });
      if (flashRef.current) {
        flashRef.current.classList.add('bigscreen-ticker-flash');
        setTimeout(() => flashRef.current?.classList.remove('bigscreen-ticker-flash'), 2000);
      }
    });
    return () => {
      socket.emit('unsubscribe', 'dashboard');
      socket.disconnect();
    };
  }, []);

  // ---- 中国地图：贵州高亮 + 站点呼吸点；点击站点切园区，点击贵州下钻省视图 ----
  const mapOption = useMemo(() => {
    localStorage.setItem('bs_map_level', mapLevel);
    const mapName = mapLevel === 'guizhou' ? 'guizhou' : 'china';
    const scatter = mapStations.map((s) => ({
      name: s.name,
      value: [s.longitude, s.latitude, s.power_kw || 0],
      stationId: s.id,
      stationType: s.type,
      onlineStatus: s.online_status,
      itemStyle: {
        color: s.online_status === '离线' ? '#5b6b8c' : (STATION_COLORS[s.type] || '#40a9ff'),
        shadowBlur: 12,
        shadowColor: s.online_status === '离线' ? 'rgba(91,107,140,0.6)' : (STATION_COLORS[s.type] || '#40a9ff'),
      },
      symbolSize: Math.max(10, Math.min(26, 10 + Math.sqrt(Math.max(0, s.power_kw || 0)) / 4)),
    }));
    // 省视图以站点质心为中心放大（站点密集区域看得清分布），全国视图固定缩放
    let geoCenter;
    let geoZoom = 1.22;
    if (mapLevel === 'guizhou') {
      if (mapStations.length > 0) {
        geoCenter = [
          mapStations.reduce((s, x) => s + x.longitude, 0) / mapStations.length,
          mapStations.reduce((s, x) => s + x.latitude, 0) / mapStations.length,
        ];
        geoZoom = 3.2;
      } else {
        geoZoom = 1.0;
      }
    }
    return {
      backgroundColor: 'transparent',
      tooltip: {
        backgroundColor: 'rgba(5,20,40,0.9)',
        borderColor: 'rgba(0,229,255,0.4)',
        textStyle: { color: '#d6ecff' },
        formatter: (p) => {
          if (p.seriesType === 'effectScatter') {
            const d = p.data;
            return `${d.name}<br/>类型：${STATION_NAMES[d.stationType] || '站点'}　状态：${d.onlineStatus}<br/>当前功率：${d.value[2] ?? '-'} kW`;
          }
          return p.name;
        },
      },
      geo: {
        map: mapName,
        roam: false,
        zoom: geoZoom,
        center: geoCenter,
        // geo 区域点击不发 click 事件，只有 geoToggleSelect → geoselectchanged，故开启单选用于贵州下钻
        selectedMode: 'single',
        label: { show: false },
        itemStyle: {
          areaColor: 'rgba(10,35,70,0.55)',
          borderColor: 'rgba(0,229,255,0.45)',
          borderWidth: 1,
          shadowColor: 'rgba(0,229,255,0.25)',
          shadowBlur: 12,
        },
        emphasis: {
          label: { show: true, color: '#7fe7ff', fontSize: 11 },
          itemStyle: { areaColor: 'rgba(0,120,200,0.45)' },
        },
        select: {
          label: { show: true, color: '#7fe7ff', fontSize: 12, fontWeight: 600 },
          itemStyle: { areaColor: 'rgba(0,180,255,0.28)', borderColor: '#29f0ff', borderWidth: 1.5 },
        },
        // 贵州高亮（全国视图）
        regions: mapLevel === 'china' ? [{
          name: '贵州省',
          selected: true,
        }] : [],
      },
      series: [
        {
          type: 'effectScatter',
          coordinateSystem: 'geo',
          rippleEffect: { brushType: 'stroke', scale: 3 },
          showEffectOn: 'render',
          zlevel: 2,
          // 站点标签默认隐藏（密集时避免叠字成块），悬浮/高亮时显示
          label: { show: false },
          emphasis: {
            label: {
              show: true, position: 'right', formatter: '{b}',
              color: '#d6f4ff', fontSize: 11, fontWeight: 600,
              textShadowColor: 'rgba(0,0,0,0.8)', textShadowBlur: 4,
            },
          },
          data: scatter,
        },
      ],
    };
  }, [mapStations, mapLevel]);

  const onMapEvents = useMemo(() => ({
    click: (params) => {
      // 点击站点呼吸点 → 进入该站点数字孪生视图
      if (params.seriesType === 'effectScatter' && params.data?.stationId) {
        setViewMode('stations');
        setParkId(params.data.stationId);
      }
    },
    // geo 区域点击只触发 geoToggleSelect（ECharts 源码层面不发 click），选中贵州即下钻省视图
    geoselectchanged: (params) => {
      if (mapLevel === 'china' && params.name === '贵州省' && params.selected?.['贵州省']) {
        setMapLevel('guizhou');
      }
    },
  }), [mapLevel]);

  // ---- 能流拓扑：保留原 graph 数据流，改发光配色 ----
  const flowChart = useMemo(() => {
    if (!flow) return {};
    const colors = { grid: '#40a9ff', pv: '#ffd666', storage: '#b37feb', charging: '#31e8a3', load: '#ff7a90' };
    const nodes = flow.nodes.map((n) => ({
      id: n.id,
      name: `${n.name}\n${n.power} kW`,
      symbolSize: n.category === 'load' ? 84 : 64,
      itemStyle: {
        color: colors[n.category],
        shadowBlur: 18,
        shadowColor: colors[n.category],
        borderColor: '#e6f7ff',
        borderWidth: 1,
      },
      category: n.category,
    }));
    const links = flow.links.filter((l) => l.value > 0).map((l) => ({
      source: l.source,
      target: l.target,
      value: l.value,
      lineStyle: {
        width: Math.max(2, l.value / 80),
        curveness: 0.2,
        color: '#29f0ff',
        shadowBlur: 10,
        shadowColor: 'rgba(41,240,255,0.8)',
      },
    }));
    return {
      backgroundColor: 'transparent',
      tooltip: {
        backgroundColor: 'rgba(5,20,40,0.9)',
        borderColor: 'rgba(0,229,255,0.4)',
        textStyle: { color: '#d6ecff' },
        formatter: (p) => p.data?.name || `${p.data?.source} → ${p.data?.target}: ${p.data?.value} kW`,
      },
      series: [{
        type: 'graph',
        layout: 'none',
        roam: false,
        label: { show: true, color: '#d6ecff', fontSize: 11 },
        // 坐标按中栏下方面板（约 480×250）紧凑排布，超出会被裁剪
        data: nodes.map((n) => ({
          ...n,
          x: { grid: 125, pv: 125, storage: 270, charging: 415, load: 268 }[n.id],
          y: { grid: 55, pv: 145, storage: 145, charging: 145, load: 225 }[n.id],
        })),
        links,
        lineStyle: { color: '#29f0ff', opacity: 0.65 },
        emphasis: { focus: 'adjacency' },
      }],
    };
  }, [flow]);

  const axisStyle = {
    axisLabel: { color: '#7ea8d8', fontSize: 10 },
    axisLine: { lineStyle: { color: 'rgba(0,229,255,0.25)' } },
    splitLine: { lineStyle: { color: 'rgba(30,74,122,0.4)' } },
  };

  // ---- 24h 能源曲线：霓虹发光线条 ----
  const trendChart = useMemo(() => ({
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis', backgroundColor: 'rgba(5,20,40,0.9)', borderColor: 'rgba(0,229,255,0.4)', textStyle: { color: '#d6ecff' } },
    legend: { data: ['负荷', '光伏', '充电枪'], textStyle: { color: '#9ecbff', fontSize: 11 }, top: 0 },
    grid: { left: 42, right: 14, top: 30, bottom: 24 },
    xAxis: { type: 'category', data: trendSeries.labels, ...axisStyle },
    yAxis: { type: 'value', name: 'kW', nameTextStyle: { color: '#7ea8d8' }, ...axisStyle },
    series: [
      { name: '负荷', type: 'line', smooth: true, connectNulls: true, showSymbol: false, data: trendSeries.load, lineStyle: { color: '#29f0ff', width: 2, shadowBlur: 12, shadowColor: 'rgba(41,240,255,0.7)' }, areaStyle: { color: 'rgba(41,240,255,0.12)' } },
      { name: '光伏', type: 'line', smooth: true, connectNulls: true, showSymbol: false, data: trendSeries.pv, lineStyle: { color: '#ffd666', width: 2, shadowBlur: 12, shadowColor: 'rgba(255,214,102,0.6)' } },
      { name: '充电枪', type: 'line', smooth: true, connectNulls: true, showSymbol: false, data: trendSeries.charging, lineStyle: { color: '#b37feb', width: 2, shadowBlur: 12, shadowColor: 'rgba(179,127,235,0.6)' } },
    ],
  }), [trendSeries]);

  // ---- 设备在线状态环形图 ----
  const deviceChart = useMemo(() => {
    const ds = overview?.device_status || { online: 0, offline: 0, fault: 0, total: 0 };
    const rate = ds.total > 0 ? ((ds.online / ds.total) * 100).toFixed(1) : null;
    return {
      backgroundColor: 'transparent',
      tooltip: { backgroundColor: 'rgba(5,20,40,0.9)', borderColor: 'rgba(0,229,255,0.4)', textStyle: { color: '#d6ecff' } },
      title: {
        text: rate === null ? '-' : `${rate}%`,
        subtext: '在线率',
        left: 'center', top: '38%',
        textStyle: { color: '#29f0ff', fontSize: 22, fontWeight: 700 },
        subtextStyle: { color: '#7ea8d8', fontSize: 11 },
      },
      legend: { bottom: 0, textStyle: { color: '#9ecbff', fontSize: 11 }, itemWidth: 10, itemHeight: 10 },
      series: [{
        type: 'pie',
        radius: ['56%', '76%'],
        center: ['50%', '46%'],
        avoidLabelOverlap: true,
        label: { show: false },
        itemStyle: { borderColor: '#050b1a', borderWidth: 2 },
        data: [
          { name: '在线', value: ds.online, itemStyle: { color: '#31e8a3', shadowBlur: 10, shadowColor: 'rgba(49,232,163,0.5)' } },
          { name: '离线', value: ds.offline, itemStyle: { color: '#5b6b8c' } },
          { name: '故障', value: ds.fault, itemStyle: { color: '#ff5b6e', shadowBlur: 10, shadowColor: 'rgba(255,91,110,0.5)' } },
        ],
      }],
    };
  }, [overview]);

  // ---- 近 30 日逐日电量趋势 ----
  const trend30Chart = useMemo(() => ({
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis', backgroundColor: 'rgba(5,20,40,0.9)', borderColor: 'rgba(0,229,255,0.4)', textStyle: { color: '#d6ecff' } },
    legend: { data: ['用电量', '光伏发电量', '充电量'], textStyle: { color: '#9ecbff', fontSize: 11 }, top: 0 },
    grid: { left: 56, right: 16, top: 30, bottom: 22 },
    xAxis: { type: 'category', data: trend30.labels, ...axisStyle },
    yAxis: { type: 'value', name: 'kWh', nameTextStyle: { color: '#7ea8d8' }, ...axisStyle },
    series: [
      { name: '用电量', type: 'line', smooth: true, connectNulls: true, showSymbol: false, data: trend30.load, lineStyle: { color: '#29f0ff', width: 2, shadowBlur: 10, shadowColor: 'rgba(41,240,255,0.6)' }, areaStyle: { color: 'rgba(41,240,255,0.08)' } },
      { name: '光伏发电量', type: 'line', smooth: true, connectNulls: true, showSymbol: false, data: trend30.pv, lineStyle: { color: '#ffd666', width: 2, shadowBlur: 10, shadowColor: 'rgba(255,214,102,0.5)' } },
      { name: '充电量', type: 'line', smooth: true, connectNulls: true, showSymbol: false, data: trend30.charging, lineStyle: { color: '#31e8a3', width: 2, shadowBlur: 10, shadowColor: 'rgba(49,232,163,0.5)' } },
    ],
  }), [trend30]);

  const tickerItems = useMemo(() => {
    // 双保险：后端已只返回活跃告警，这里再挡一次 resolved/已处理，防止 socket 推送或旧缓存混入
    const active = liveAlarms.filter((a) => !['resolved', '已解决', '已处理'].includes(a.status));
    if (!active.length) return [{ key: 'empty', text: '暂无告警，系统运行正常' }];
    return active.map((a) => ({
      key: `${a.id}-${a.occurred_at}`,
      level: a.level,
      text: `[${formatAlarmTime(a.occurred_at)}] ${a.level} · ${a.title}（${a.status}）`,
    }));
  }, [liveAlarms]);

  // 告警列表（右侧栏，垂直滚动）
  const alarmListItems = useMemo(
    () => liveAlarms.filter((a) => !['resolved', '已解决', '已处理'].includes(a.status)),
    [liveAlarms]
  );

  // 调度事件看板：需求响应事件 + 调度指令合并
  // 展示规则：进行中的事件优先，其余按时间倒序；已结束且超过 30 天的历史事件不上屏
  const eventItems = useMemo(() => {
    const now = Date.now();
    const dr = (overview?.events?.demand_response || []).map((e) => ({
      key: `dr-${e.id}`,
      title: e.title,
      type: `需求响应·${e.type}`,
      status: e.status,
      time: e.start_time,
      power: e.target_power_kw,
      finished: DR_FINISHED.includes(e.status),
    }));
    const dp = (overview?.events?.dispatch || []).map((c) => ({
      key: `dp-${c.id}`,
      title: c.command_id,
      type: `调度·${c.type}`,
      status: c.status,
      time: c.created_at,
      power: c.target_power,
      finished: DP_FINISHED.includes(c.status),
    }));
    return [...dr, ...dp]
      .filter((e) => !(e.finished && now - new Date(e.time).getTime() > EVENT_WINDOW_MS))
      .sort((a, b) => (a.finished - b.finished) || (new Date(b.time) - new Date(a.time)))
      .slice(0, 8);
  }, [overview]);

  if (loading && !overview) {
    return <div className="bs-viewport"><Spin size="large" className="bigscreen-spin" /></div>;
  }

  const o = overview?.overview || {};
  const vpp = overview?.vpp || {};
  const park = overview?.park || {};
  const alarmStats = overview?.alarms || {};
  const alarmLevels = overview?.alarm_levels || { emergency: 0, critical: 0, warning: 0, info: 0 };
  const resourceStats = overview?.resource_stats || { parks: 0, pv: 0, storage: 0, charging_piles: 0, charging_guns: 0 };
  const alarmLevelMax = Math.max(1, alarmLevels.emergency, alarmLevels.critical, alarmLevels.warning, alarmLevels.info);
  const topMax = Math.max(1, ...topStations.list.map((t) => t.value || 0));
  const selectedStation = stationOptions.find((s) => s.id === parkId);
  const isStationView = viewMode === 'stations' && !!selectedStation;
  const twinViewer = isStationView ? TWIN_VIEWERS[parkId] : null;
  const currentParkName = selectedStation?.name || park.name;
  const stationPiles = isStationView
    ? chargingStatus.filter((p) => p.station_name === selectedStation.name)
    : [];

  return (
    <div className="bs-viewport">
      <div
        className="bs-stage"
        style={{
          width: DESIGN_W,
          height: DESIGN_H,
          left: `calc(50% - ${DESIGN_W / 2}px)`,
          top: `calc(50% - ${DESIGN_H / 2}px)`,
          transform: `scale(${scale})`,
        }}
      >
        {/* ===== 顶部：页签 / 发光大标题+时钟 / 操作 ===== */}
        <header className="bs-header">
          <div className="bs-header-left">
            <button type="button" className={`bs-park-tab ${viewMode === 'network' ? 'active' : ''}`} onClick={() => { setViewMode('network'); setParkId(0); }}>全网总览</button>
            <button type="button" className={`bs-park-tab ${viewMode === 'stations' ? 'active' : ''}`} onClick={() => { setViewMode('stations'); if (!selectedStation) setParkId(null); }}>各站点</button>
            {viewMode === 'stations' && (
              <select
                className="bs-station-select"
                value={selectedStation?.id || ''}
                onChange={(e) => { const id = Number(e.target.value); if (id) setParkId(id); }}
              >
                <option value="" disabled>请选择场站</option>
                {stationOptions.map((station) => <option key={station.id} value={station.id}>{shortStationName(station.name)}</option>)}
              </select>
            )}
          </div>
          <div className="bs-header-center">
            <div className="bs-title">好运数智能源管理平台</div>
            <div className="bs-subtitle">{currentParkName || '多园区能源管控中心'} · QIANHAOYUN ENERGY</div>
          </div>
          <div className="bs-header-right">
            <span className="bs-clock">{clock}</span>
            <Button type="text" icon={<ReloadOutlined />} onClick={() => { loadData(parkId); loadSlow(); }} className="bigscreen-btn">刷新</Button>
            <Button type="text" icon={<FullscreenExitOutlined />} onClick={() => navigate('/dashboard')} className="bigscreen-btn">退出大屏</Button>
          </div>
        </header>

        {/* ===== 告警滚动条 ===== */}
        <div ref={flashRef} className="bigscreen-ticker-wrap">
          <div className="bigscreen-ticker-label">
            <AlertOutlined /> 实时告警
            <Tag color="red" style={{ marginLeft: 8 }}>{alarmStats.active || 0} 条未处理</Tag>
          </div>
          <div className="bigscreen-ticker-track">
            <div className="bigscreen-ticker-content">
              {[...tickerItems, ...tickerItems].map((item, i) => (
                <span key={`${item.key}-${i}`} className={`bigscreen-ticker-item level-${LEVEL_STYLE[item.level] || 'info'}`}>
                  {item.text}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* ===== 核心 KPI 条 ===== */}
        <div className="bs-kpi-strip">
          {[
            { label: '园区总负荷', value: o.total_load, unit: 'kW', color: '#29f0ff' },
            { label: '光伏出力', value: o.pv_power, unit: 'kW', color: '#ffd666' },
            { label: '储能SOC', value: o.storage_soc, unit: '%', color: '#b37feb' },
            { label: '充电枪功率', value: o.charging_power, unit: 'kW', color: '#31e8a3' },
            { label: '市电功率', value: o.grid_power, unit: 'kW', color: '#40a9ff' },
            { label: '自给率', value: o.self_sufficiency_rate, unit: '%', color: '#95de64' },
            { label: 'VPP可调', value: vpp.available_regulation, unit: 'kW', color: '#ff9c6e' },
          ].map((item) => (
            <div key={item.label} className="bs-kpi-item">
              <span className="bs-kpi-label">{item.label}</span>
              <span className="bs-kpi-value" style={{ color: item.color }}>
                <RollNumber value={item.value} decimals={1} />
                <em>{item.unit}</em>
              </span>
            </div>
          ))}
        </div>

        {/* ===== 三栏主体 ===== */}
        <div className="bs-main">
          {/* 左栏 */}
          <div className="bs-col bs-col-left">
            <Panel title="资源规模统计" className="bs-panel-resources">
              <div className="bs-resource-grid">
                {[
                  { label: '园区/网络', value: resourceStats.parks, color: '#40a9ff' },
                  { label: '光伏系统', value: resourceStats.pv, color: '#ffd666' },
                  { label: '储能系统', value: resourceStats.storage, color: '#b37feb' },
                ].map((it) => (
                  <div key={it.label} className="bs-resource-item">
                    <div className="bs-resource-value" style={{ color: it.color }}>
                      <RollNumber value={it.value} />
                    </div>
                    <div className="bs-resource-label">{it.label}</div>
                  </div>
                ))}
                {/* 充电桩格：桩/枪两行（枪级推送口径，枪数由后端按枪级表计实算） */}
                <div className="bs-resource-item">
                  <div className="bs-resource-value bs-resource-dual" style={{ color: '#31e8a3' }}>
                    <span><RollNumber value={resourceStats.charging_piles} /><em> 台桩</em></span>
                    <span><RollNumber value={resourceStats.charging_guns} /><em> 把枪</em></span>
                  </div>
                  <div className="bs-resource-label">充电桩/枪</div>
                </div>
              </div>
            </Panel>
            <Panel title="设备在线状态" className="bs-panel-device">
              {(overview?.device_status?.total || 0) > 0
                ? <BSChart option={deviceChart} />
                : <EmptyBox text="暂无设备台账" />}
            </Panel>
            <Panel title="告警分级统计" className="bs-panel-alarmlevel" extra={`活跃 ${alarmStats.active || 0} 条`}>
              <div className="bs-alarmlevel-list">
                {[
                  { label: '紧急', value: alarmLevels.emergency, cls: 'emergency' },
                  { label: '严重', value: alarmLevels.critical, cls: 'critical' },
                  { label: '警告', value: alarmLevels.warning, cls: 'warning' },
                  { label: '提示', value: alarmLevels.info, cls: 'info' },
                ].map((it) => (
                  <div key={it.label} className="bs-alarmlevel-row">
                    <span className={`bs-alarmlevel-name lv-${it.cls}`}>{it.label}</span>
                    <div className="bs-alarmlevel-bar-wrap">
                      <div className={`bs-alarmlevel-bar lv-${it.cls}`} style={{ width: `${(it.value / alarmLevelMax) * 100}%` }} />
                    </div>
                    <span className="bs-alarmlevel-num"><RollNumber value={it.value} /></span>
                  </div>
                ))}
              </div>
            </Panel>
          </div>

          {/* 中栏 */}
          <div className="bs-col bs-col-center">
            {viewMode === 'stations' && !selectedStation ? (
              <Panel title="各站点数字孪生" className="bs-panel-map">
                <EmptyBox text="请在左上角选择场站" />
              </Panel>
            ) : (
              <>
                {isStationView ? (
                  <Panel
                    title={`${stationPanel === 'video' ? '视频监控' : stationPanel === 'fusion' ? '虚实融合' : '数字孪生'} · ${currentParkName}`}
                    className={`bs-panel-map ${stationPanel === 'twin' ? 'bs-panel-twin' : 'bs-panel-video'}`}
                    extra={(
                      <span className="bs-view-switch">
                        <button type="button" className={stationPanel === 'twin' ? 'active' : ''} onClick={() => setStationPanel('twin')}>数字孪生</button>
                        <button type="button" className={stationPanel === 'video' ? 'active' : ''} onClick={() => setStationPanel('video')}>视频监控</button>
                        <button type="button" className={stationPanel === 'fusion' ? 'active' : ''} onClick={() => setStationPanel('fusion')}>虚实融合</button>
                      </span>
                    )}
                  >
                    {stationPanel === 'video' ? (
                      <StationVideoGrid spaceId={parkId} />
                    ) : stationPanel === 'fusion' ? (
                      parkId === 25 ? <StationVideoFusion spaceId={parkId} /> : <EmptyBox text="该站点尚未完成相机标定" />
                    ) : twinViewer ? (
                      <iframe key={twinViewer} className="bs-twin-frame" src={twinViewer} title={`${currentParkName}数字孪生`} allow="fullscreen" />
                    ) : <EmptyBox text="该站点实景模型待接入" />}
                  </Panel>
                ) : (
                  <Panel
                    title={mapLevel === 'guizhou' ? '站点分布 · 贵州省' : '站点分布 · 全国'}
                    className="bs-panel-map"
                    extra={mapLevel === 'guizhou'
                      ? <button type="button" className="bs-back-btn" onClick={() => setMapLevel('china')}><GlobalOutlined /> 返回全国</button>
                      : <span>
                          <button type="button" className="bs-back-btn" onClick={() => setMapLevel('guizhou')}><GlobalOutlined /> 下钻贵州</button>
                          <span style={{ marginLeft: 10 }}>{mapStations.length === 0 ? '暂无带坐标站点' : `${mapStations.length} 个站点`}</span>
                        </span>}
                  >
                    <BSChart option={mapOption} onEvents={onMapEvents} />
                  </Panel>
                )}
                {(!isStationView || stationPanel !== 'video') && (
                  <div className="bs-center-bottom">
                    <Panel title={`能源流向拓扑 · ${currentParkName || '-'}`} className="bs-panel-flow">
                      {flow && flow.nodes?.length
                        ? <BSChart option={flowChart} />
                        : <EmptyBox />}
                    </Panel>
                    <Panel title="24小时能源曲线" className="bs-panel-trend24">
                      {trendSeries.labels.length > 0
                        ? <BSChart option={trendChart} />
                        : <EmptyBox text="暂无遥测数据" />}
                    </Panel>
                  </div>
                )}
              </>
            )}
          </div>

          {/* 右栏 */}
          <div className="bs-col bs-col-right">
            <Panel title="实时告警" className="bs-panel-alarms" extra={`${alarmListItems.length} 条活跃`}>
              {alarmListItems.length === 0 ? <EmptyBox text="暂无告警，系统运行正常" /> : (
                <div className="bs-alarm-scroll">
                  <div className="bs-alarm-scroll-inner">
                    {[...alarmListItems, ...alarmListItems].map((a, i) => (
                      <div key={`${a.id}-${i}`} className="bs-alarm-row">
                        <span className={`bs-alarm-level lv-${LEVEL_STYLE[a.level] || 'info'}`}>{a.level || '提示'}</span>
                        <span className="bs-alarm-title" title={a.title}>{a.title}</span>
                        <span className="bs-alarm-time">{formatAlarmTime(a.occurred_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Panel>
            {isStationView ? (
              <Panel title="充电桩实时状态" className="bs-panel-top10" extra={`${stationPiles.length} 台 · 10秒刷新`}>
                {stationPiles.length === 0 ? <EmptyBox text="暂无桩枪数据" /> : (
                  <div className="bs-pile-list">
                    {stationPiles.map((pile) => (
                      <div key={pile.code} className="bs-pile-row">
                        <div className="bs-pile-main">
                          <span className="bs-pile-name" title={pile.name}>{pile.name}</span>
                          <span className="bs-pile-guns">{(pile.guns || []).map((g) => `${g.gun_no}枪 ${g.status || '暂无数据'}`).join(' · ')}</span>
                        </div>
                        <span className="bs-pile-power"><RollNumber value={pile.current_power} decimals={1} /> kW</span>
                      </div>
                    ))}
                  </div>
                )}
              </Panel>
            ) : (
              <Panel title="站点充电量 TOP10" className="bs-panel-top10" extra={`今日 · ${topStations.unit}`}>
                {topStations.list.length === 0 ? <EmptyBox /> : (
                  <div className="bs-top10-list">
                    {topStations.list.map((t) => (
                      <div key={t.rank} className="bs-top10-row">
                        <span className={`bs-top10-rank ${t.rank <= 3 ? 'top' : ''}`}>{t.rank}</span>
                        <span className="bs-top10-name" title={t.name}>{shortStationName(t.name)}</span>
                        <div className="bs-top10-bar-wrap">
                          <div className="bs-top10-bar" style={{ width: `${(t.value / topMax) * 100}%` }} />
                        </div>
                        <span className="bs-top10-value"><RollNumber value={t.value} decimals={1} /></span>
                      </div>
                    ))}
                  </div>
                )}
              </Panel>
            )}
            <Panel title="需求响应 / 调度事件" className="bs-panel-events">
              {eventItems.length === 0 ? <EmptyBox text="暂无调度事件" /> : (
                <div className="bs-event-list">
                  {eventItems.map((e) => (
                    <div key={e.key} className="bs-event-row">
                      <span className="bs-event-status">{e.status}</span>
                      <div className="bs-event-main">
                        <div className="bs-event-title" title={e.title}>{e.title}</div>
                        <div className="bs-event-meta">{e.type} · {formatAlarmTime(e.time)}{e.power != null ? ` · ${e.power} kW` : ''}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="bs-event-footnote">进行中优先 · 仅显示近 30 天</div>
            </Panel>
          </div>
        </div>

        {/* ===== 底部：30 日趋势 + 今日统计 ===== */}
        <div className="bs-footer">
          <Panel title="近30日能源趋势" className="bs-panel-trend30">
            {trend30.labels.length > 0
              ? <BSChart option={trend30Chart} />
              : <EmptyBox text="暂无历史遥测" />}
          </Panel>
          <div className="bs-today">
            <div className="bs-today-item"><span>今日用电</span><b><RollNumber value={overview?.today?.total_consumption} decimals={1} /> kWh</b></div>
            <div className="bs-today-item"><span>光伏发电</span><b><RollNumber value={overview?.today?.total_pv_yield} decimals={1} /> kWh</b></div>
            <div className="bs-today-item"><span>充电量</span><b><RollNumber value={overview?.today?.total_charging} decimals={1} /> kWh</b></div>
            <div className="bs-today-item" title="按已结束充电订单及配置因子进行内部管理测算，非核证减排量或碳资产"><span>今日充电测算减排</span><ChargingCarbonValue value={overview?.carbon?.today_reduction_tco2e} decimals={3} /></div>
            <div className="bs-today-item" title="按已结束充电订单及配置因子进行内部管理测算，非核证减排量或碳资产"><span>本月充电测算减排</span><ChargingCarbonValue value={overview?.carbon?.month_reduction_tco2e} /></div>
            <div className="bs-today-item" title="按已结束充电订单及配置因子进行内部管理测算，非核证减排量或碳资产"><span>累计充电测算减排</span><ChargingCarbonValue value={overview?.carbon?.total_reduction_tco2e} /></div>
            {vpp.storage_mw > 10 && <div className="bs-today-item"><span>储能规模</span><b>{vpp.storage_mw} MW</b></div>}
          </div>
        </div>
      </div>
      <Modal
        title={activeCamera ? `玉舍站 · ${activeCamera.name}` : ''}
        open={!!activeCamera} footer={null} width={860}
        onCancel={() => setActiveCamera(null)} destroyOnClose
      >
        {activeCamera && <EzvizPlayer camera={activeCamera} />}
      </Modal>
    </div>
  );
}
